import type { LabelElement } from './types'
import { DATA_FIELDS, ELEMENT_TYPE_NAMES, FONT_FAMILIES } from './types'

interface Props {
  element: LabelElement | null
  onUpdate: (prop: keyof LabelElement, value: unknown) => void
  onDelete: () => void
  onRotate: (delta: number) => void
}

function FieldSelect({
  value,
  onChange,
  allowEmpty,
}: {
  value: string
  onChange: (v: string) => void
  allowEmpty?: boolean
}) {
  return (
    <select
      className="w-full border rounded-md px-2 py-1.5 text-sm font-mono"
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      {allowEmpty && <option value="">— не задано —</option>}
      {DATA_FIELDS.map(k => (
        <option key={k} value={k}>{k}</option>
      ))}
    </select>
  )
}

export function PropertiesPanel({ element, onUpdate, onDelete, onRotate }: Props) {
  if (!element) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400 text-center text-sm">
          Выберите элемент<br />для редактирования
        </p>
      </div>
    )
  }

  const isBarcode =
    element.type === 'datamatrix' ||
    element.type === 'datamatrix_ka' ||
    element.type === 'gs1_128' ||
    element.type === 'ean13'

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Тип элемента:</label>
        <input
          type="text"
          className="w-full border rounded-md px-2 py-1.5 text-sm bg-gray-50"
          value={ELEMENT_TYPE_NAMES[element.type] || element.type}
          disabled
        />
      </div>

      {element.type === 'text' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Префикс:</label>
            <input
              type="text"
              className="w-full border rounded-md px-2 py-1.5 text-sm font-mono"
              value={element.label || ''}
              onChange={e => onUpdate('label', e.target.value)}
              placeholder="Напр. Артикул: "
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Можно писать {'{product_code}'} прямо в префиксе, или выбрать поле ниже
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Поле данных:</label>
            <FieldSelect
              value={element.field || ''}
              allowEmpty
              onChange={v => onUpdate('field', v)}
            />
          </div>
        </>
      )}

      {isBarcode && (
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Поле данных (key):</label>
          <FieldSelect
            value={element.field || ''}
            onChange={v => onUpdate('field', v)}
          />
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Позиция:</label>
          <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-xs text-gray-500">X (мм):</span>
            <input
              type="number"
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={(() => {
                const v = Math.round(element.x * 10) / 10
                return v === 0 ? '' : String(v)
              })()}
              step={0.1}
              onChange={e => {
                const v = Number(e.target.value)
                onUpdate('x', isNaN(v) ? 0 : Math.round(v * 10) / 10)
              }}
            />
          </div>
          <div>
            <span className="text-xs text-gray-500">Y (мм):</span>
            <input
              type="number"
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={(() => {
                const v = Math.round(element.y * 10) / 10
                return v === 0 ? '' : String(v)
              })()}
              step={0.1}
              onChange={e => {
                const v = Number(e.target.value)
                onUpdate('y', isNaN(v) ? 0 : Math.round(v * 10) / 10)
              }}
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Размер:</label>
          <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-xs text-gray-500">Ширина (мм):</span>
            <input
              type="number"
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={element.width === 0 ? '' : String(element.width)}
              step={0.5}
              onChange={e => {
                const v = Number(e.target.value)
                onUpdate('width', isNaN(v) ? 0 : v)
              }}
            />
          </div>
          <div>
            <span className="text-xs text-gray-500">Высота (мм):</span>
            <input
              type="number"
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={element.height === 0 ? '' : String(element.height)}
              step={0.5}
              onChange={e => {
                const v = Number(e.target.value)
                onUpdate('height', isNaN(v) ? 0 : v)
              }}
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Вращение:</label>
        <div className="flex gap-2 items-center">
          <select
            className="flex-1 border rounded-md px-2 py-1.5 text-sm"
            value={element.rotation || 0}
            onChange={e => onUpdate('rotation', Number(e.target.value))}
          >
            <option value={0}>0° (по умолчанию)</option>
            <option value={90}>90° (вправо)</option>
            <option value={180}>180° (вверх ногами)</option>
            <option value={270}>270° (влево)</option>
          </select>
          <button
            className="p-1.5 border rounded hover:bg-gray-100 text-lg"
            title="Повернуть влево"
            onClick={() => onRotate(-90)}
          >↺</button>
          <button
            className="p-1.5 border rounded hover:bg-gray-100 text-lg"
            title="Повернуть вправо"
            onClick={() => onRotate(90)}
          >↻</button>
        </div>
      </div>

      {element.type === 'text' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Шрифт:</label>
            <select
              className="w-full border rounded-md px-2 py-1.5 text-sm"
              value={element.fontFamily || 'dejavu_sans'}
              onChange={e => onUpdate('fontFamily', e.target.value)}
            >
              {FONT_FAMILIES.map(f => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Размер шрифта:</label>
            <input
              type="number"
              className="w-full border rounded-md px-2 py-1.5 text-sm"
              value={(element.fontSize || 0) === 0 ? '' : String(element.fontSize)}
              onChange={e => {
                const raw = e.target.value
                if (raw === '') {
                  onUpdate('fontSize', 0)
                } else {
                  const v = Number(raw)
                  onUpdate('fontSize', isNaN(v) ? 0 : v)
                }
              }}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Начертание:</label>
            <div className="flex gap-1">
              <button
                className={`flex-1 py-1.5 border rounded text-sm font-bold ${element.fontWeight === 'bold' ? 'bg-blue-500 text-white border-blue-500' : 'hover:bg-gray-100'}`}
                onClick={() => onUpdate('fontWeight', element.fontWeight === 'bold' ? 'normal' : 'bold')}
              >
                B
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Выравнивание:</label>
            <div className="flex gap-1">
              {(['left', 'center', 'right'] as const).map(a => (
                <button
                  key={a}
                  className={`flex-1 py-1.5 border rounded text-sm ${element.align === a ? 'bg-blue-500 text-white border-blue-500' : 'hover:bg-gray-100'}`}
                  onClick={() => onUpdate('align', a)}
                >
                  {a === 'left' ? 'L' : a === 'center' ? 'C' : 'R'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Перенос:</label>
            <select
              className="w-full border rounded-md px-2 py-1.5 text-sm"
              value={element.wrapMode || 'wrap_words'}
              onChange={e => onUpdate('wrapMode', e.target.value)}
            >
              <option value="wrap_words">По словам</option>
              <option value="no_wrap">Без переноса</option>
              <option value="break_all">По символам</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Обрезка по ширине:</label>
            <select
              className="w-full border rounded-md px-2 py-1.5 text-sm"
              value={element.overflowX || 'visible'}
              onChange={e => onUpdate('overflowX', e.target.value)}
            >
              <option value="visible">Видно за полем</option>
              <option value="clip">Обрезать</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Обрезка по высоте:</label>
            <select
              className="w-full border rounded-md px-2 py-1.5 text-sm"
              value={element.overflowY || 'visible'}
              onChange={e => onUpdate('overflowY', e.target.value)}
            >
              <option value="visible">Видно за полем</option>
              <option value="clip">Обрезать</option>
            </select>
          </div>
        </>
      )}

      <button
        className="w-full mt-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-md transition-colors"
        onClick={onDelete}
      >
        Удалить элемент
      </button>
    </div>
  )
}
